import {PdpDataLayer} from "@/types/dto/pdp/pdp-data-layer";
import {StaticContent} from "@/types/dto/pdp/pdp-contentful";
import {TourDate} from "@/types/dto/pdp/pdp-dates-inclusions-pricing";
import {TourHeader} from "@/types/dto/pdp/pdp-header-about";

const tourStatusFactory = (newTour?: boolean, comingSoon?: boolean) => {
  if (newTour) {
    return "New Tour";
  }
  if (comingSoon) {
    return "Coming Soon";
  }
  return "Active";
};

const formatTourName = (tourName?: string, tourSubtitle?: string) => {
  if (!tourName) return "";
  return `${tourName}${tourSubtitle ? ` ${tourSubtitle}` : ""}`;
};

const cleanPrice = (price: string): number => {
  if (!price) return 0;

  return parseInt(price.replace(/\D/g, ""), 10);
};

const productSubgroupTypeFactory = (newTour: boolean, comingSoon: boolean) => {
  if (comingSoon) return "Coming Soon Tour";
  if (newTour) return "New Tour";
  return "Active Tour";
};

const ecommerceItemsFactory = (
  selectedDate: TourDate | null,
  tourInfo: {
    tourId: string;
    tourName: string;
    country: string;
    continent: string;
    travelStyle: string;
  }
) => [
  ...(selectedDate?.singlePrice
    ? [{
      item_id: tourInfo?.tourId,
      item_name: tourInfo?.tourName,
      price: cleanPrice(selectedDate?.singlePrice),
      quantity: 1,
      item_category: tourInfo?.country,
      item_category2: tourInfo?.continent,
      item_category3: tourInfo?.travelStyle?.split(", ")?.[0],
      item_variant: selectedDate?.packageDateId
    }]
  : []),
  ...(selectedDate?.doublePrice
    ? [{
      item_id: tourInfo?.tourId,
      item_name: tourInfo?.tourName,
      price: cleanPrice(selectedDate?.doublePrice),
      quantity: 2,
      item_category: tourInfo?.country,
      item_category2: tourInfo?.continent,
      item_category3: tourInfo?.travelStyle?.split(", ")?.[0],
      item_variant: selectedDate?.packageDateId
    }]
  : [])
];

const pdpDataLayerFactory = (
  staticData: StaticContent,
  tourHeaderData: TourHeader | undefined,
  selectedDate: TourDate | null,
  isAgent: boolean
): PdpDataLayer => ({
  pageLoadData: {
    event: "dataLayerPush",
    pageInformation: {
      pageGroupType: "Product Page",
      productSubgroupType: productSubgroupTypeFactory(tourHeaderData?.newTour ?? false, false) ?? "", // TODO replace false with coming soon data (JF)
      isAgent: isAgent?.toString() ?? "false"
    },
    tourInformation: {
      productId: staticData?.tourContent?.neoId ?? "",
      productName: formatTourName(staticData?.tourContent?.tourTitle, staticData?.tourContent?.tourSubtitle) ?? "",
      productYear: selectedDate?.departureYear ?? "",
      packageID: selectedDate?.packageId ?? "",
      packageDateId: selectedDate?.packageDateId ?? "",
      packageDate: {
        startDate: selectedDate?.startDate ?? "",
        endDate: selectedDate?.endDate ?? ""
      },
      tourStyle: tourHeaderData?.travelStyles?.join(", ") ?? "",
      tourArea: staticData?.tourContent?.continent ?? "",
      // TODO add coming soon logic when added to api (JF)
      tourStatus: tourStatusFactory(tourHeaderData?.newTour, false) ?? "",
      activityLevel: tourHeaderData?.activityLevelNumber?.toString() ?? "",
      totalDays: tourHeaderData?.days ?? 0,
      tourPrice: cleanPrice(selectedDate?.doublePrice ?? "") ?? 0,
      tourSavings: tourHeaderData?.offer ?? "",
      group_size: tourHeaderData?.groupSizeAvg ?? 0
    }
  },
  ecommerceData: {
    event: "view_item",
    ecommerce: {
      currency: staticData?.tourContent?.currencyCode ?? "",
      items: ecommerceItemsFactory(
        selectedDate,
        {
          tourId: staticData?.tourContent?.neoId ?? "",
          tourName: formatTourName(staticData?.tourContent?.tourTitle, staticData?.tourContent?.tourSubtitle) ?? "",
          country: staticData?.tourContent?.country ?? "",
          continent: staticData?.tourContent?.continent ?? "",
          travelStyle: tourHeaderData?.travelStyles?.join(", ") ?? ""
        }
      )
    }
  }
});

export default pdpDataLayerFactory;